import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Shield,
  Trash2,
  Save,
  Eye,
  Loader2,
  LayoutGrid,
  Layers,
  Search,
  Sparkles,
  ArrowLeft,
  ChevronRight,
  Pencil,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { type PanelLevel } from "../../config/panelCatalog";
import {
  listAccess,
  createAccess,
  updateAccess,
  deleteAccess,
  getAccessMembers,
  type AccessProfile,
  type AccessMember,
  type PanelPermission,
} from "../../api/access";
import {
  blankPermissions,
  enabledCountForCatalog,
  panelCatalogForPlan,
  restrictPermissionsToCatalog,
} from "../../utils/accessPermissions";
import { PermissionGridEditor } from "../../components/PermissionGridEditor";
import { InUseGuardModal, type GuardPerson } from "../../components/InUseGuardModal";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";
import { useAuth } from "../../contexts/AuthContext";

type Draft = {
  id: string | null; // null → unsaved new profile
  name: string;
  description: string;
  permissions: Record<string, PanelPermission>;
};

const SERIF = '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif';


const newDraft = (): Draft => ({
  id: null,
  name: "",
  description: "",
  permissions: blankPermissions(),
});

export default function AccessManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const limitedPlan = user?.subscription?.workspace_mode === "limited";
  const availablePanels = useMemo(() => panelCatalogForPlan(limitedPlan), [limitedPlan]);
  const availablePanelCount = availablePanels.filter((panel) => !panel.feature).length;
  const availableGroupCount = new Set(availablePanels.map((panel) => panel.group)).size;
  // Managers can open this screen ONLY to flip the "AI Assistant (Lio)" toggle:
  // the grid renders read-only for them apart from that row, and create/delete/
  // rename are hidden. This flag is UX only — the hrms backend narrows a
  // manager's save to the leo cell regardless (routers/access.py).
  const leoOnly = (user?.role || "").toLowerCase() === "manager";
  const [list, setList] = useState<AccessProfile[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null); // non-null → editor page open
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [members, setMembers] = useState<AccessMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  // Set when a delete is refused → drives the centered "in use" popup.
  const [blocked, setBlocked] = useState<{ name: string; people: GuardPerson[] } | null>(null);
  // In-app confirmation dialog (replaces native window.confirm).
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setList(await listAccess());
    } catch {
      /* keep current list; silent on load failure */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Load the "who" — employees assigned to the profile being edited.
  useEffect(() => {
    if (!draft?.id) {
      setMembers([]);
      return;
    }
    let alive = true;
    setMembersLoading(true);
    getAccessMembers(draft.id)
      .then((m) => alive && setMembers(m))
      .catch(() => alive && setMembers([]))
      .finally(() => alive && setMembersLoading(false));
    return () => {
      alive = false;
    };
  }, [draft?.id]);

  // ----- navigation between list page and editor page -----
  const openNew = () =>
    setDraft({
      ...newDraft(),
      permissions: restrictPermissionsToCatalog(blankPermissions(), availablePanels),
    });
  const openEdit = (p: AccessProfile) =>
    setDraft({
      id: p.id,
      name: p.name,
      description: p.description || "",
      permissions: restrictPermissionsToCatalog(blankPermissions(p.permissions), availablePanels),
    });
  const backToList = () => setDraft(null);

  const setVisible = (key: string, visible: boolean) =>
    setDraft((d) =>
      d ? { ...d, permissions: { ...d.permissions, [key]: { ...d.permissions[key], visible } } } : d
    );

  const setLevel = (key: string, level: PanelLevel) =>
    setDraft((d) =>
      d ? { ...d, permissions: { ...d.permissions, [key]: { ...d.permissions[key], level } } } : d
    );

  const visibleCount = draft
    ? enabledCountForCatalog(draft.permissions, availablePanels)
    : 0;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? list.filter((p) => p.name.toLowerCase().includes(q)) : list;
  }, [list, query]);

  const save = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      toast.error("Give this access a name");
      return;
    }
    setSaving(true);
    try {
      if (draft.id) {
        await updateAccess(draft.id, {
          name: draft.name,
          description: draft.description,
          permissions: restrictPermissionsToCatalog(draft.permissions, availablePanels),
        });
        toast.success("Access updated");
      } else {
        await createAccess({
          name: draft.name,
          description: draft.description,
          permissions: restrictPermissionsToCatalog(draft.permissions, availablePanels),
        });
        toast.success("Access created");
      }
      await load();
      setDraft(null); // back to the list page
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Jump to the employee's edit page so the manager can move them onto a
  // different access before deleting this one.
  const goToEmployeeEdit = (employeeId: string) => {
    navigate("/employees", { state: { editEmployeeId: employeeId } });
  };

  const remove = async () => {
    if (!draft?.id) {
      setDraft(null);
      return;
    }
    // Block deletion while anyone still holds this access — deleting it would
    // strip their permissions. Open the centered popup so the manager can jump
    // to reassigning them first.
    if (members.length > 0) {
      setBlocked({
        name: draft.name,
        people: members.map((m) => ({ id: m.id, name: m.name, sub: m.employeeID || m.role || m.email })),
      });
      return;
    }
    const accessId = draft.id;
    setConfirm({
      title: `Delete access "${draft.name}"?`,
      confirmLabel: "Delete",
      tone: "danger",
      onConfirm: async () => {
        try {
          await deleteAccess(accessId);
          toast.success("Access deleted");
          setDraft(null);
          await load();
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || "Delete failed");
        }
      },
    });
  };

  const tintMap: Record<string, string> = {
    emerald: "bg-emerald-50 text-emerald-600 ring-emerald-100",
    sky: "bg-sky-50 text-sky-600 ring-sky-100",
    violet: "bg-violet-50 text-violet-600 ring-violet-100",
    amber: "bg-amber-50 text-amber-600 ring-amber-100",
  };

  const animStyle = (
    <style>{`
      @keyframes amUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      .am-root .am-anim { animation: amUp .5s cubic-bezier(.2,.7,.2,1) both; }
      .am-root .am-card { transition: box-shadow .25s ease, transform .25s ease, border-color .2s ease; }
      .am-root .am-stat:hover { transform: translateY(-2px); box-shadow: 0 10px 30px -12px rgba(16,24,40,.18); }
      .am-root .am-rowlink { transition: background .15s ease, border-color .15s ease; }
      .am-root .am-rowlink:hover { background: rgba(16,185,129,.05); }
      .am-root .am-prow { transition: background .15s ease; }
      @media (prefers-reduced-motion: reduce) { .am-root .am-anim { animation: none; } }
    `}</style>
  );

  // ===========================================================================
  // EDITOR PAGE (separate "page" — opens like the Employee form)
  // ===========================================================================
  if (draft) {
    return (
      <div className="am-root max-w-5xl mx-auto">
        {animStyle}

        {/* Toolbar */}
        <div className="am-anim flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <button
              onClick={backToList}
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              aria-label="Back to access profiles"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight">
                {draft.id ? "Edit Access" : "New Access"}
              </h1>
              <p className="text-xs text-gray-400">
                {leoOnly
                  ? "Manager view — you can change only the AI Assistant (Lio) toggle."
                  : "Choose which panels & pages this profile can see."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!leoOnly && (
              <button
                onClick={remove}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-700 px-3 h-10 rounded-xl hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" /> {draft.id ? "Delete" : "Discard"}
              </button>
            )}
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 h-10 rounded-xl shadow-sm shadow-emerald-600/20 disabled:opacity-60 transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {draft.id ? "Save Changes" : "Create Access"}
            </button>
          </div>
        </div>

        {/* Details card */}
        <div className="am-anim am-card bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-5" style={{ animationDelay: ".05s" }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                Access Name
              </label>
              <input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. Sales Executive"
                autoFocus
                disabled={leoOnly}
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400 disabled:bg-gray-50 disabled:text-gray-400"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                Description (optional)
              </label>
              <input
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="What this access is for"
                disabled={leoOnly}
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400 disabled:bg-gray-50 disabled:text-gray-400"
              />
            </div>
          </div>
          <div className="mt-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1">
              <Eye className="w-3 h-3" /> {visibleCount} / {availablePanelCount} panels enabled
            </span>
          </div>
        </div>

        {/* Assigned people — who is using this access */}
        {draft.id && (
          <div className="am-anim am-card bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-5" style={{ animationDelay: ".08s" }}>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-emerald-600" />
              <h3 className="text-sm font-semibold text-gray-800">Assigned People</h3>
              <span className="inline-flex items-center text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                {members.length}
              </span>
            </div>
            {membersLoading ? (
              <div className="text-sm text-gray-400 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : members.length === 0 ? (
              <p className="text-sm text-gray-400">No one is assigned this access yet.</p>
            ) : (
              <>
                <p className="text-xs text-gray-400 mb-2 -mt-1">
                  Tap someone to change their access.
                </p>
                <div className="flex flex-wrap gap-2">
                  {members.map((m) => (
                    <button
                      type="button"
                      key={m.id}
                      onClick={() => goToEmployeeEdit(m.id)}
                      className="group/chip flex items-center gap-2 border border-gray-100 rounded-xl pl-1.5 pr-3 py-1.5 bg-gray-50/60 hover:bg-emerald-50 hover:border-emerald-200 transition-colors text-left"
                      title={`Edit ${m.name}'s access`}
                    >
                      <div className="w-7 h-7 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-xs font-bold shrink-0">
                        {m.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-800 leading-tight truncate group-hover/chip:text-emerald-700">
                          {m.name}
                        </div>
                        <div className="text-[11px] text-gray-400 leading-tight truncate">
                          {m.employeeID || m.role || ""}
                        </div>
                      </div>
                      <Pencil className="w-3 h-3 text-gray-300 group-hover/chip:text-emerald-500 shrink-0 ml-0.5" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Permission table card */}
        <div style={{ animationDelay: ".1s" }}>
          <PermissionGridEditor
            permissions={draft.permissions}
            setVisible={setVisible}
            setLevel={setLevel}
            panelCatalog={availablePanels}
            editableKeys={leoOnly ? ["leo"] : undefined}
          />
        </div>

        <InUseGuardModal
          open={!!blocked}
          onClose={() => setBlocked(null)}
          entity="access"
          name={blocked?.name || ""}
          people={blocked?.people || []}
          onEditPerson={(id) => {
            setBlocked(null);
            goToEmployeeEdit(id);
          }}
        />

        {/* In-app confirmation dialog (replaces native window.confirm). */}
        <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
      </div>
    );
  }

  // ===========================================================================
  // LIST PAGE (default — like the Employees list)
  // ===========================================================================
  const totalAssigned = list.reduce((s, p) => s + (p.assigned_count || 0), 0);
  const stats = [
    { label: "Access Profiles", value: list.length, Icon: Shield, tint: "emerald" },
    { label: "Panels Available", value: availablePanelCount, Icon: LayoutGrid, tint: "sky" },
    { label: "Sections", value: availableGroupCount, Icon: Layers, tint: "violet" },
    { label: "People Assigned", value: totalAssigned, Icon: Users, tint: "amber" },
  ] as const;

  return (
    <div className="am-root max-w-6xl mx-auto">
      {animStyle}

      {/* Header banner */}
      <div
        className="am-anim relative overflow-hidden rounded-3xl px-6 py-7 mb-6"
        style={{
          background:
            "radial-gradient(1200px 200px at 0% 0%, rgba(16,185,129,.14), transparent 60%), linear-gradient(135deg, #0f3d33 0%, #12564a 48%, #0c7a63 100%)",
        }}
      >
        <div
          aria-hidden
          className="absolute -right-10 -top-16 w-64 h-64 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(255,255,255,.10), transparent 70%)" }}
        />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center ring-1 ring-white/40 shadow-sm shrink-0">
              <Shield className="w-6 h-6 text-emerald-700" />
            </div>
            <div>
              {/* Rendered as a div (not h1) + inline color so Teamlio's global
                  dark heading style can't override the on-banner white text. */}
              <div
                className="text-[26px] leading-tight font-bold tracking-tight"
                style={{ color: "#ffffff" }}
              >
                Access{" "}
                <span style={{ fontFamily: SERIF, fontStyle: "italic", fontWeight: 500, color: "#ffffff" }}>
                  Management
                </span>
              </div>
              <p className="text-sm mt-1 max-w-md" style={{ color: "rgba(255,255,255,0.82)" }}>
                Create access profiles and choose exactly which panels &amp; pages each one
                can see after login.
              </p>
            </div>
          </div>
          {!leoOnly && (
            <button
              onClick={openNew}
              className="inline-flex items-center gap-2 bg-white text-emerald-800 hover:bg-emerald-50 font-semibold text-sm px-4 h-10 rounded-xl shadow-sm transition-colors"
            >
              <Plus className="w-4 h-4" /> New Access
            </button>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {stats.map((s, i) => (
          <div
            key={s.label}
            className="am-anim am-card am-stat bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3"
            style={{ animationDelay: `${0.05 * (i + 1)}s` }}
          >
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ring-1 ${tintMap[s.tint]}`}>
              <s.Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 truncate">
                {s.label}
              </div>
              <div className="text-2xl font-bold text-gray-900 leading-none mt-0.5">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Profiles list card */}
      <div className="am-anim am-card bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" style={{ animationDelay: ".25s" }}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 flex-wrap">
          <span className="text-sm font-semibold text-gray-700">Access Profiles</span>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search profiles…"
                className="h-9 w-56 pl-8 pr-3 rounded-lg border border-gray-200 text-sm bg-gray-50/60 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400"
              />
            </div>
            {!leoOnly && (
              <button
                onClick={openNew}
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3.5 h-9 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" /> New
              </button>
            )}
          </div>
        </div>

        {/* Column header */}
        <div className="hidden sm:grid grid-cols-[1fr_240px_40px] gap-3 px-5 py-2.5 bg-gray-50/60 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          <span>Profile</span>
          <span className="text-right pr-1">People &middot; Panels</span>
          <span />
        </div>

        {loading ? (
          <div className="p-8 text-sm text-gray-400 flex items-center gap-2 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4 ring-1 ring-emerald-100">
              <Sparkles className="w-7 h-7 text-emerald-500" />
            </div>
            <h3 className="text-base font-semibold text-gray-800">
              {query ? "No matching profiles" : "No access profiles yet"}
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              {query ? "Try a different search." : "Create your first access profile to get started."}
            </p>
            {!query && !leoOnly && (
              <button
                onClick={openNew}
                className="mt-5 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 h-10 rounded-xl transition-colors"
              >
                <Plus className="w-4 h-4" /> New Access
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map((p) => (
              <button
                key={p.id}
                onClick={() => openEdit(p)}
                className="am-rowlink w-full text-left grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_240px_40px] items-center gap-3 px-5 py-3.5"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center text-sm font-bold shrink-0">
                    {p.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">{p.name}</div>
                    <div className="text-xs text-gray-400 truncate">
                      {p.description || "No description"}
                    </div>
                  </div>
                </div>
                <div className="hidden sm:flex items-center justify-end gap-2">
                  <span
                    className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-2.5 py-1"
                    title={`${p.assigned_count ?? 0} people use this access`}
                  >
                    <Users className="w-3 h-3" /> {p.assigned_count ?? 0}
                  </span>
                  <span className="inline-flex items-center text-xs font-medium text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1">
                    {enabledCountForCatalog(p.permissions, availablePanels)} panels
                  </span>
                </div>
                <div className="flex items-center justify-end gap-1 text-gray-300">
                  <Pencil className="w-3.5 h-3.5 text-gray-400" />
                  <ChevronRight className="w-4 h-4" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
